// Pointer events -> viewport calls, for one chart panel.
//
// Pure: no DOM, no React. Panel.tsx feeds it from the overlay's handlers, and
// gestures.test.ts runs pointer sequences through it.
//
//   mouse  drag pans, hover moves the cursor, leaving clears it, a double
//          click resets: exactly what the chart did before touch existed.
//   touch  one finger moves the cursor (lifting it clears the cursor); two
//          fingers zoom around where they were and pan with their midpoint;
//          a double tap resets. A vertical swipe is not ours: the browser
//          scrolls the page (touch-action: pan-y on the svg) and cancels the
//          pointer, which clears the cursor. The dblclick a browser adds
//          after a double tap is ignored: the taps above are the one rule
//          (and the one that tests and the e2e suite can tell apart from the
//          browser's).

export const DOUBLE_TAP_MS = 300;
export const TAP_SLOP_PX = 10;
export const DOUBLE_TAP_DISTANCE_PX = 30;
export const MIN_PINCH_PX = 12;
const ZOOM_EPSILON = 0.001;

export interface GesturePointer {
    pointerId: number;
    /** 'touch' is a finger; anything else, '' included, is treated as a mouse. */
    pointerType: string;
    button: number;
    clientX: number;
    clientY: number;
    timeStamp: number;
}

export interface GestureEnv {
    /** The time under a screen x. */
    timeAt: (clientX: number) => number;
    /** Milliseconds one pixel of the plot covers. */
    msPerPx: number;
    setCursor: (t: number | null) => void;
    panBy: (deltaMs: number) => void;
    /** factor < 1 zooms in, > 1 zooms out (zoomDomain's convention). */
    zoomAt: (t: number, factor: number) => void;
    reset: () => void;
}

export interface ChartGestures {
    /** True when the caller should capture the pointer. */
    down: (p: GesturePointer) => boolean;
    move: (p: GesturePointer) => void;
    up: (p: GesturePointer) => void;
    cancel: (p: GesturePointer) => void;
    leave: (p: GesturePointer) => void;
    doubleClick: () => void;
}

interface PointerEventLike {
    pointerId: number;
    pointerType?: string;
    button?: number;
    clientX: number;
    clientY: number;
    timeStamp: number;
}

export function toGesturePointer(e: PointerEventLike): GesturePointer {
    return {
        pointerId: e.pointerId,
        pointerType: e.pointerType ?? '',
        button: e.button ?? 0,
        clientX: e.clientX,
        clientY: e.clientY,
        timeStamp: e.timeStamp,
    };
}

interface Finger {
    x: number;
    y: number;
    startX: number;
    startY: number;
    downAt: number;
    moved: boolean;
}

const isTouch = (p: GesturePointer) => p.pointerType === 'touch';

export function createChartGestures(getEnv: () => GestureEnv): ChartGestures {
    let mouseDrag: { x: number } | null = null;
    const fingers = new Map<number, Finger>();
    // Distance and midpoint (screen x) of the two fingers at the last move.
    let pinch: { dist: number; mid: number } | null = null;
    // A pinch happened: the finger that is left reads nothing and counts as
    // no tap until every finger is up.
    let quiet = false;
    let lastTap: { at: number; x: number; y: number } | null = null;
    // The kind of pointer that last went down: a dblclick after a finger is
    // the browser's echo of a double tap, not a mouse double click.
    let lastDownType = '';

    const pinchOf = (): { dist: number; mid: number } => {
        const [a, b] = [...fingers.values()];
        return { dist: Math.abs(a.x - b.x), mid: (a.x + b.x) / 2 };
    };

    return {
        down(p) {
            const env = getEnv();
            lastDownType = p.pointerType;
            if (!isTouch(p)) {
                if (p.button !== 0) return false;
                mouseDrag = { x: p.clientX };
                return true;
            }
            if (fingers.size >= 2 || fingers.has(p.pointerId)) return false;
            fingers.set(p.pointerId, {
                x: p.clientX,
                y: p.clientY,
                startX: p.clientX,
                startY: p.clientY,
                downAt: p.timeStamp,
                moved: false,
            });
            if (fingers.size === 1) {
                quiet = false;
                env.setCursor(env.timeAt(p.clientX));
            } else {
                quiet = true;
                lastTap = null;
                env.setCursor(null);
                pinch = pinchOf();
            }
            return true;
        },

        move(p) {
            const env = getEnv();
            if (!isTouch(p)) {
                if (mouseDrag) {
                    const dx = p.clientX - mouseDrag.x;
                    if (dx !== 0) {
                        mouseDrag.x = p.clientX;
                        env.panBy(-dx * env.msPerPx);
                    }
                    return;
                }
                env.setCursor(env.timeAt(p.clientX));
                return;
            }
            const finger = fingers.get(p.pointerId);
            if (!finger) return;
            finger.x = p.clientX;
            finger.y = p.clientY;
            if (
                Math.abs(p.clientX - finger.startX) > TAP_SLOP_PX ||
                Math.abs(p.clientY - finger.startY) > TAP_SLOP_PX
            ) {
                finger.moved = true;
            }
            if (fingers.size === 1) {
                if (!quiet) env.setCursor(env.timeAt(p.clientX));
                return;
            }
            if (!pinch) return;
            const next = pinchOf();
            let scale = 1;
            if (pinch.dist >= MIN_PINCH_PX && next.dist >= MIN_PINCH_PX) {
                const factor = pinch.dist / next.dist;
                if (Math.abs(factor - 1) > ZOOM_EPSILON) {
                    // Zoom around the time that was under the old midpoint ...
                    env.zoomAt(env.timeAt(pinch.mid), factor);
                    scale = factor;
                }
            }
            // ... then follow the midpoint. The caller's msPerPx is that of
            // its last render, i.e. before this zoom: scale it by the zoom.
            const dx = next.mid - pinch.mid;
            if (dx !== 0) env.panBy(-dx * env.msPerPx * scale);
            pinch = next;
        },

        up(p) {
            if (!isTouch(p)) {
                mouseDrag = null;
                return;
            }
            const env = getEnv();
            const finger = fingers.get(p.pointerId);
            if (!finger) return;
            fingers.delete(p.pointerId);
            pinch = null;
            if (fingers.size > 0) return;
            const wasQuiet = quiet;
            quiet = false;
            env.setCursor(null);
            const tapped =
                !wasQuiet &&
                !finger.moved &&
                p.timeStamp - finger.downAt <= DOUBLE_TAP_MS;
            if (!tapped) {
                lastTap = null;
                return;
            }
            if (
                lastTap !== null &&
                p.timeStamp - lastTap.at <= DOUBLE_TAP_MS &&
                Math.hypot(finger.x - lastTap.x, finger.y - lastTap.y) <=
                    DOUBLE_TAP_DISTANCE_PX
            ) {
                lastTap = null;
                env.reset();
                return;
            }
            lastTap = { at: p.timeStamp, x: finger.x, y: finger.y };
        },

        cancel(p) {
            if (!isTouch(p)) {
                mouseDrag = null;
                return;
            }
            if (!fingers.delete(p.pointerId)) return;
            pinch = null;
            lastTap = null;
            if (fingers.size === 0) {
                quiet = false;
                getEnv().setCursor(null);
            }
        },

        leave(p) {
            if (!isTouch(p) && mouseDrag === null) getEnv().setCursor(null);
        },

        doubleClick() {
            if (lastDownType === 'touch') return;
            getEnv().reset();
        },
    };
}
